import { useState, useSyncExternalStore } from 'react'
import styles from './DebugConsole.module.css'
import { clearLog, getLogEntries, subscribeLog, type LogEntry } from '../../platform/debugLog'
import { environmentReport } from '../../platform/environment'
import { copyText } from '../../platform/clipboard'
import { t } from '../strings'

function useLogEntries(): readonly LogEntry[] {
  // getLogEntries devuelve siempre el mismo array; la longitud y el último id fuerzan el render.
  const version = useSyncExternalStore(subscribeLog, () => {
    const entries = getLogEntries()
    return `${entries.length}:${entries[entries.length - 1]?.id ?? 0}`
  })
  void version
  return getLogEntries()
}

function formatEntry(entry: LogEntry): string {
  const time = new Date(entry.at).toLocaleTimeString('es-ES', { hour12: false })
  return `${time} [${entry.level}] ${entry.message}`
}

export function DebugConsole() {
  const [open, setOpen] = useState(false)
  const [copied, setCopied] = useState(false)
  const entries = useLogEntries()
  const errors = entries.filter((entry) => entry.level === 'error').length

  if (!open) {
    return (
      <button className={styles.toggle} onClick={() => setOpen(true)}>
        {t.debug.toggle}
        {errors > 0 && <span className={styles.badge}>{errors}</span>}
      </button>
    )
  }

  const env = environmentReport()
  const envText = Object.entries(env)
    .map(([key, value]) => `${key}: ${String(value)}`)
    .join('\n')

  const copy = async () => {
    await copyText(`${envText}\n\n${entries.map(formatEntry).join('\n')}`)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  return (
    <section className={styles.panel} aria-label={t.debug.title}>
      <div className={styles.bar}>
        <strong>{t.debug.title}</strong>
        <button onClick={copy}>{copied ? t.debug.copied : t.debug.copy}</button>
        <button onClick={clearLog}>{t.debug.clear}</button>
        <button onClick={() => setOpen(false)}>{t.debug.close}</button>
      </div>
      <pre className={styles.list}>
        <div className={styles.env}>{envText}</div>
        {entries.length === 0 && <div className={styles.entry}>{t.debug.empty}</div>}
        {entries.map((entry) => (
          <div
            key={entry.id}
            className={
              entry.level === 'error'
                ? styles.error
                : entry.level === 'warn'
                  ? styles.warn
                  : styles.entry
            }
          >
            {formatEntry(entry)}
          </div>
        ))}
      </pre>
    </section>
  )
}
